import { z } from "zod";

export enum Algorithm {
  "TOP_PRODUCTS" = "TOP_PRODUCTS",
  "PERSONAL" = "PERSONAL",
  "CART" = "CART",
  "STYLE_WITH" = "STYLE_WITH",
  "ALTERNATIVES" = "ALTERNATIVES",
  "UPSELL" = "UPSELL",
  "NEWEST_PRODUCTS" = "NEWEST_PRODUCTS",
  "FAVORITES" = "FAVORITES",
  "MORE_FROM_SERIES" = "MORE_FROM_SERIES",
  "RECENTLY_VIEWED" = "RECENTLY_VIEWED",
  "ADD_TO_CART_RECS" = "ADD_TO_CART_RECS",
}

export const productRule = z.object({
  id: z.string(),
  productRules: z.string(),
  limit: z.number().optional(),
  algorithm: z.nativeEnum(Algorithm).optional(),
  params: z
    .object({
      productKey: z.string().optional(),
      cart: z.array(z.string()).optional(),
    })
    .optional(),
});

export type ProductRule = z.infer<typeof productRule>;

export type ProductRuleConfig = {
  attribute: string;
  values?: string[];
  operation?: "excl" | "incl";
  algorithm?: ProductRule["algorithm"];
  params?: ProductRule["params"];
  id?: string;
};

/**
 * Elevate rejects the whole request body when a recommendation list asks for
 * more products than this ("must be less than or equal to 200"), which takes
 * down every other list in the same request along with it.
 */
export const ELEVATE_MAX_RECOMMENDATION_LIMIT = 200;

/**
 * Split a key list into batches Elevate will accept. Spread the batches into
 * `createProductRules` so an oversized relation becomes several recommendation
 * lists rather than one rejected request.
 */
export function chunkRuleValues(
  values: string[],
  size = ELEVATE_MAX_RECOMMENDATION_LIMIT,
): string[][] {
  if (values.length <= size) {
    return [values];
  }

  const batches: string[][] = [];
  for (let index = 0; index < values.length; index += size) {
    batches.push(values.slice(index, index + size));
  }

  return batches;
}

export function createProductRules(
  ...args: ProductRuleConfig[]
): ProductRule[] {
  return args.map(
    (
      {
        attribute,
        values = ["noop"],
        operation = "incl",
        algorithm,
        params,
        id,
      },
      index,
    ) => {
      const productRules = `rule ${operation} ${attribute} { ${values.map((value) => `"${value}"`).join(" ")} }`;
      return {
        id: id ?? "dynamic_" + index,
        productRules,
        limit: Math.min(values.length, ELEVATE_MAX_RECOMMENDATION_LIMIT),
        algorithm,
        params,
      };
    },
  );
}
